import * as PIXI from 'pixi.js-legacy';
import { metricToImperial } from '../util/measures';
import platform from '../util/platform';
import constants from '../settings/constants';
import { ceil } from '../util/round';
import { selectUseMetric } from '../state/dexfSettings/dexfSettingsSelectors';
import store from '../state';

export default class MeasurementLine extends PIXI.Container {
  constructor({
    ratio,
    length,
    isMobile,
    textInside,
    textUnderLine,
    skipFirstSerif,
    isVertical,
    isTable,
    isDepth,
    size,
  }) {
    super();

    this.ratio = ratio;
    this.length = length;
    this.isMobile = isMobile;
    this.textInside = textInside;
    this.textUnderLine = textUnderLine;
    this.skipFirstSerif = skipFirstSerif;
    this.isVertical = isVertical;
    this.isTable = isTable;
    this.isDepth = isDepth;
    this.size = size;
    this.useMetricMeasures = selectUseMetric(store.getState());

    this.draw();
  }

  getSerifPoints(serif, lineLength) {
    /*
      if the length of the line is smaller than text background
      start drawing serif from where text background ends to
      prevent ugly fading issue. Serifs showing behind text background
    */

    let minY = lineLength > this.background.width ? -serif / 2 : 0;
    let maxY = serif / 2;

    if (this.textUnderLine) {
      minY = -serif / 2;
      maxY = lineLength > this.background.width ? serif / 2 : 0;
    }

    if (!!this.isDepth) {
      minY = -serif / 2;
      maxY = serif / 2;
    }

    return [
      [
        [0, minY],
        [0, maxY],
      ],
      [
        [lineLength, minY],
        [lineLength, maxY],
      ],
    ];
  }

  draw() {
    const length = this.length * this.ratio;

    let adjustedLength;

    if (!!this.isDepth) {
      adjustedLength = this.useMetricMeasures
        ? ceil(this.size, 10)
        : ceil(this.size, 1);
    } else {
      adjustedLength = this.useMetricMeasures
        ? ceil(this.length, 10)
        : ceil(this.length, 1);
    }

    const measure = this.useMetricMeasures
      ? adjustedLength / 10
      : metricToImperial(adjustedLength).onlyInches;
    const textContent = `${measure}`;

    const style = new PIXI.TextStyle({
      fontSize: platform.isKiosk ? 20 : 12,
      fontWeight: 900,
      fontFamily: 'NotoIKEALatin, Verdana, sans-serif',
      lineHeight: platform.isKiosk ? 1.2 : 1.33,
      trim: this.isMobile,
      fill: '#ffffff',
    });

    if (!this.label) {
      this.label = new PIXI.Text(textContent, style);
    } else {
      this.label.text = textContent;
    }
    this.label.resolution = 3;

    const textPaddingHorizontal = 6;
    const textPaddingVertical = platform.isKiosk ? 8 : 4;

    this.label.x =
      this.isVertical || this.isTable
        ? (length - this.label.height) / 2
        : (length - this.label.width) / 2;

    if (!this.isVertical) {
      if (!this.textUnderLine) {
        this.label.y = this.textInside
          ? textPaddingVertical
          : -textPaddingVertical - this.label.height;
      } else {
        this.label.y = textPaddingVertical;
      }
    } else {
      this.label.y = -textPaddingHorizontal;
    }

    this.background = new PIXI.Graphics();
    const thickness = !!this.isVertical ? 0.5 : 1.0;
    this.background.lineStyle(thickness, 0x111111);
    this.background.beginFill(0x111111);

    !this.isVertical &&
      !this.isTable &&
      this.background.drawRoundedRect(
        this.label.x - textPaddingHorizontal,
        this.label.y - textPaddingVertical,
        this.label.width + textPaddingHorizontal * 2.0,
        this.label.height + textPaddingVertical * 2.0,
        2
      );

    !!this.isVertical &&
      this.background.drawRoundedRect(
        this.label.x - textPaddingVertical,
        this.label.y - textPaddingHorizontal - this.label.width,
        this.label.height + textPaddingVertical * 2.0,
        this.label.width + textPaddingHorizontal * 2.0,
        2
      );

    const serif = constants.MEASUREMENTS_LINE_SERIF * this.ratio;
    const lineThickness = this.isMobile ? 1 : 2;
    const depthLineThickness = this.isMobile ? 2 : 3;
    const serifPoints = this.getSerifPoints(serif, length);
    if (!this.isDepth) {
      this.line = new PIXI.Graphics()
        .lineStyle(lineThickness, 0x111111, 1)
        .moveTo(0, 0)
        .lineTo(length, 0);
    } else {
      this.line = new PIXI.Graphics()
        .lineStyle(depthLineThickness, 0x111111, 1)
        .moveTo(0, 0)
        .lineTo(length, 0);
    }

    if (!this.skipFirstSerif) {
      this.line.moveTo(...serifPoints[0][0]).lineTo(...serifPoints[0][1]);
    }
    this.line.moveTo(...serifPoints[1][0]).lineTo(...serifPoints[1][1]);

    if (!!this.isTable) {
      this.background.drawRoundedRect(
        this.label.x - textPaddingVertical,
        this.label.y - textPaddingVertical,
        this.label.height + textPaddingVertical * 2.0,
        this.label.width + textPaddingHorizontal * 2.0,
        2
      );

      this.rotation = Math.PI / 2;
      this.label.pivot = new PIXI.Point(this.label.width, 0);
      this.label.rotation = Math.PI / -2;
      this.background.y = this.background.y + this.background.width;
      this.label.y = this.label.y + this.background.width;
    } else if (!!this.isVertical) {
      this.label.pivot = new PIXI.Point(0, 0);
      this.label.rotation = Math.PI / -2;
    } else if (!!this.isDepth) {
      if (!!this.isMobile) {
        this.background.x = this.background.x + this.label.width;
        this.label.x = this.label.x + this.background.x;
      } else if (platform.isKiosk) {
        this.background.x = this.background.x + this.label.width;
        this.background.y = this.background.y + this.background.height;
        this.label.x = this.label.x + this.background.x;
        this.label.y = this.label.y + this.background.y;
      } else {
        this.background.y = this.background.y + this.background.width;
        this.label.y = this.label.y + this.background.y;
      }
      this.line.angle =
        Math.sin(constants.OBLIQUE_ANGLE) * constants.ROOM_DEPTH * 0.33;
      this.line.skew.x = -5.25;
    }

    this.background.endFill();

    this.textContainer = new PIXI.Container();
    this.textContainer.addChild(this.background, this.label);

    this.addChild(this.textContainer);
    this.addChild(this.line);
  }
}
